import React, { useState, useEffect } from 'react';
import { Link } from 'react-scroll';
import { AppBar, Toolbar, Button, Box, IconButton, List, ListItem, ListItemText} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const Header = ({ onLanguageChange }) => {
  const { t } = useTranslation(); 
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeLink, setActiveLink] = useState('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoVisible, setLogoVisible] = useState(false);
  const [languageVisible, setLanguageVisible] = useState(false);
  const [linksVisibility, setLinksVisibility] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState('en');

  const handleLanguageChange = (language) => {
    setSelectedLanguage(language); 
    onLanguageChange(language); 
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 960) {
        setMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setLogoVisible(true);
      setLanguageVisible(true);
      const sections = ['home', 'about', 'projects', 'skills', 'contact'];
      sections.forEach((section, index) => {
        setTimeout(() => {
          setLinksVisibility((prev) => ({
            ...prev,
            [section]: true,
          }));
        }, index * 300);
      });
    }, 500); 
  }, []);

  const handleSetActiveLink = (link) => {
    setActiveLink(link);
    setMenuOpen(false);
  };

  const sections = ['home', 'about', 'projects', 'skills', 'contact'];
  const sectionLabels = {
    home: t('home'),
    about: t('about'),
    projects: t('projects'),
    skills: t('skills'),
    contact: t('contact'),
  };

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        height: '12vh',
        display: 'flex',
        justifyContent: 'center',
        backdropFilter: isScrolled ? 'blur(15px)' : 'none',
        backgroundColor: isScrolled ? '#00232c' : 'transparent',
        transition: 'all 0.3s ease-out',
        boxShadow: isScrolled ? '0px 10px 10px rgba(9, 5, 29, 0.171)' : 0,
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {/* Logo a la izquierda */}
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <img
            src="assets/logo4.png"
            alt="Logo"
            style={{
              width: '100%',
              height: '60%',
              objectFit: 'contain',
              transform: logoVisible ? 'translateY(0)' : 'translateY(-50px)',
              opacity: logoVisible ? 1 : 0,
              transition: 'transform 1s ease-out, opacity 1s ease-out',
            }}
            onClick={() => window.location.reload()}
          />
         <Box
          sx={{
            display: 'inline-flex',
            border: '2px solid transparent',
            borderRadius: '5px', 
            transform: languageVisible ? 'translateY(0)' : 'translateY(-50px)',
            opacity: languageVisible ? 1 : 0,
            transition: 'transform 1s ease-out, opacity 1s ease-out',
          }}
        >
          <Box
            sx={{
              cursor: 'pointer',
              padding: {xs:'0.3rem 0.8rem', sm:'0.4rem 0.9rem', md:'0.5rem 1rem'},
              borderRadius: '5px',
              fontSize: {xs: '12px', sm: '14px', md:'16px'},
              color: selectedLanguage === 'es' ? 'white' : '#40e0d0',
              backgroundColor: selectedLanguage === 'es' ? '#40e0d0' : 'transparent',
              fontWeight:  selectedLanguage === 'es' ? 'bold' : 'normal',
              '&:hover': {
                color: 'white',
                fontWeight: 'bold'
              },
            }}
            onClick={() => handleLanguageChange('es')}
          >
            ES
          </Box>

          <Box
            sx={{
              cursor: 'pointer',
              padding: {xs:'0.3rem 0.8rem', sm:'0.4rem 0.9rem', md:'0.5rem 1rem'},
              borderRadius: '5px',
              fontSize: {xs: '12px', sm: '14px', md:'16px'},
              color: selectedLanguage === 'en' ? 'white' : '#40e0d0',
              backgroundColor: selectedLanguage === 'en' ? '#40e0d0' : 'transparent',
              fontWeight:  selectedLanguage === 'en' ? 'bold' : 'normal',
              '&:hover': {
                color: 'white',
                fontWeight: 'bold'
              },
            }}
            onClick={() => handleLanguageChange('en')}
          >
            EN
          </Box>
        </Box>
      </Box>


        {/* Enlaces a la derecha - Desktop */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            gap: 4,
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {sections.map((section) => (
            <Button
              key={section}
              component={Link}
              to={section}
              smooth={true}
              duration={500}
              onClick={() => handleSetActiveLink(section)}
              sx={{
                color: activeLink === section ? '#40e0d0' : 'white',
                position: 'relative',
                fontSize: '1rem',
                opacity: linksVisibility[section] ? 1 : 0,
                transform: linksVisibility[section] ? 'translateY(0)' : 'translateY(-100px)',
                transition: 'opacity 0.5s ease-out, transform 0.5s ease-out',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '1px',
                  left: '0',
                  width: activeLink === section ? '100%' : '0%',
                  height: '5px',
                  backgroundColor: '#40e0d0',
                  borderRadius: '16px',
                  transition: 'width 0.3s ease-out',
                },
                '&:hover::after': {
                  width: '100%',
                },
              }}
            >
              {sectionLabels[section]}
            </Button>
          ))}

        </Box>

        {/* Menú de hamburguesa / X - Mobile */}
        <Box sx={{ display: { xs: 'flex', sm: 'flex', md: 'none' } }}>
          <IconButton color="inherit" onClick={handleMenuToggle}>
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Box>
      </Toolbar>

      {/* Menú desplegable debajo del AppBar */}
      {menuOpen && (
        <Box
          sx={{
            backgroundColor: '#00232c',
            position: 'absolute',
            top: '10vh',
            left: 0,
            right: 0,
            zIndex: 1,
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <List sx={{ display: 'flex', flexDirection: 'column', width: '100%', alignItems: 'center' }}>
            {sections.map((section, index) => (
              <ListItem key={section}>
                <Link
                  to={section}
                  smooth={true}
                  duration={500}
                  onClick={() => handleSetActiveLink(section)}
                  style={{ width: '100%', textDecoration: 'none' }}
                >
                  <ListItemText
                    primary={sectionLabels[section]}
                    sx={{
                      color: activeLink === section ? '#40e0d0' : 'white',
                      textAlign: 'center'
                    }}
                  />
                </Link>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </AppBar>
  );
};

export default Header;
